import { listingTypes, propertyTypes } from "../../../../../agent/src/common/types";
import { mapLeadType, mapPipelinePositionId, mapSource } from "../../../utils/valueMap";
import type { LeadEventType, RentalsType } from "../shared/types";

export type BitrixRentalsType = "rent" | "sale" | "general" | "default";

export const BITRIX_RENTALS_TYPES: Array<{
  id: BitrixRentalsType;
  name: string;
  description: string;
  backingTypes: RentalsType[];
}> = [
  {
    id: "rent",
    name: "Arriendo",
    description:
      "Se aplica cuando el prospecto busca alquilar, sin discriminar entre residencial o comercial.",
    backingTypes: ["rentLiving", "rentBusiness"],
  },
  {
    id: "sale",
    name: "Venta",
    description: "Se aplica cuando el prospecto busca comprar.",
    backingTypes: ["sale"],
  },
  {
    id: "general",
    name: "Captación, Mantenimiento y Administrativo",
    description:
      "Se aplica a conversaciones de los pipelines de captación, mantenimiento y administrativo (sin inmueble asociado). También se resuelve por categoría de transferencia.",
    backingTypes: ["general"],
  },
  {
    id: "default",
    name: "Default",
    description:
      "Se aplica cuando no hay inmueble asociado al prospecto o no se puede determinar el tipo de negocio. Es el fallback.",
    backingTypes: ["default"],
  },
];

export type BitrixLiteral = string | number | boolean | null;

export type BitrixPathExpr = {
  mode: "path";
  path: string;
};

export type BitrixSwitchTransform =
  | { type: "none" }
  | { type: "arrayItem"; index: number }
  | { type: "splitItem"; separator: string; index: number };

export type BitrixSwitchCase = {
  match: string;
  output: BitrixLiteral;
};

export type BitrixSwitchExpr = {
  mode: "switch";
  source: {
    path: string;
    transform?: BitrixSwitchTransform;
  };
  cases: BitrixSwitchCase[];
  default: BitrixLiteral;
};

export type BitrixIfOperator =
  | "equals"
  | "notEquals"
  | "greaterThan"
  | "lessThan"
  | "greaterOrEqual"
  | "lessOrEqual"
  | "exists"
  | "notExists";

export type BitrixIfBranch =
  | { kind: "literal"; value: BitrixLiteral }
  | { kind: "path"; path: string };

export type BitrixIfExpr = {
  mode: "if";
  condition: {
    path: string;
    operator: BitrixIfOperator;
    compareValue?: string;
  };
  then: BitrixIfBranch;
  else: BitrixIfBranch;
};

export type BitrixLookupKey = {
  path: string;
  label?: string;
};

export type BitrixLookupRow = {
  match: string;
  output: BitrixLiteral;
};

// Multi-key contains lookup against a stored table of `match → output` rows.
// At evaluation time, runs N+1 relaxation passes:
//   Pass 1: first row whose `match` (normalized) contains every key's value.
//   Pass i in 2..N+1: first row whose `match` contains only the (i-1)th key.
//   Otherwise → `default`.
// Within a pass, top-to-bottom row order breaks ties. Normalization is the
// same as switch (NFD, strip diacritics, lowercase, strip whitespace).
export type BitrixLookupExpr = {
  mode: "lookup";
  keys: BitrixLookupKey[];
  rows: BitrixLookupRow[];
  default: BitrixLiteral;
};

export type BitrixFieldExpr =
  | BitrixLiteral
  | BitrixPathExpr
  | BitrixSwitchExpr
  | BitrixIfExpr
  | BitrixLookupExpr;

export type BitrixExprMode = "literal" | "path" | "switch" | "if" | "lookup";

export type BitrixCustomFieldEntry = {
  // Suffix of the Bitrix user field (e.g. "1727189949"). The UI adds the
  // `UF_CRM_` prefix at save time.
  key: string;
  value: BitrixFieldExpr;
  // Optional free-text label shown only in the Palomma dashboard — never sent
  // to Bitrix. Helps humans remember what a given UF_CRM_* slot represents.
  label?: string;
};

// Ensure exactly one `UF_CRM_` prefix regardless of what the user typed.
// - "1234" → "UF_CRM_1234"
// - "UF_CRM_1234" → "UF_CRM_1234"
// - "UF_CRM_UF_CRM_1234" → "UF_CRM_1234" (tolerates a paste-duplicate)
export const normalizeUfCrmKey = (raw: string): string => {
  const trimmed = raw.trim();
  let suffix = trimmed;
  while (suffix.startsWith("UF_CRM_")) {
    suffix = suffix.slice("UF_CRM_".length);
  }
  return suffix === "" ? "" : `UF_CRM_${suffix}`;
};

export const stripUfCrmPrefix = (raw: string): string => {
  let suffix = raw.trim();
  while (suffix.startsWith("UF_CRM_")) {
    suffix = suffix.slice("UF_CRM_".length);
  }
  return suffix;
};

export type BitrixEntity = "lead" | "deal";

export type BitrixCreateAction = {
  // "lead" (default) → crm.lead.add. "deal" → crm.deal.add. Picks the matching
  // connection token (createLead vs createDeal) at runtime.
  entity?: BitrixEntity;
  title: string;
  comments?: string;
  sourceId?: string;
  assignedById?: BitrixFieldExpr;
  contactPhone?: BitrixFieldExpr;
  contactEmail?: BitrixFieldExpr;
  contactDefaults?: {
    fallbackLastName?: string;
    phoneValueType?: string;
    emailValueType?: string;
  };
  // UF_CRM_* sent with crm.contact.add. Bitrix keeps contact user fields in a
  // namespace separate from lead/deal ones, so a field here needs its own
  // contact-side id. Create-only — there is no contact update path.
  contactCustomFields?: Record<string, BitrixFieldExpr>;
  contactCustomFieldLabels?: Record<string, string>;
  customFields?: Record<string, BitrixFieldExpr>;
  // Palomma-only annotations keyed by the same UF_CRM_* names as customFields.
  customFieldLabels?: Record<string, string>;
  // Raw top-level Bitrix fields without UF_CRM_ prefix (CATEGORY_ID, TYPE_ID,
  // STAGE_ID, …). Required for entity="deal"; optional otherwise.
  standardFields?: Record<string, BitrixFieldExpr>;
  standardFieldLabels?: Record<string, string>;
};

export type BitrixUpdateAction = {
  entity?: BitrixEntity;
  customFields?: Record<string, BitrixFieldExpr>;
  customFieldLabels?: Record<string, string>;
  standardFields?: Record<string, BitrixFieldExpr>;
  standardFieldLabels?: Record<string, string>;
};

export type BitrixEventMapping = {
  // Legacy: single event. Kept optional so existing records persisted before
  // event grouping still deserialize without loss.
  event?: LeadEventType;
  events?: LeadEventType[];
  create?: BitrixCreateAction;
  update?: BitrixUpdateAction;
};

export const getMappingEvents = (mapping: BitrixEventMapping): LeadEventType[] => {
  if (mapping.events && mapping.events.length > 0) return mapping.events;
  if (mapping.event) return [mapping.event];
  return [];
};

export const BITRIX_SUGGESTED_PATHS: Array<{ path: string; description: string }> = [
  { path: "lead.id", description: "ID del prospecto en Palomma" },
  { path: "lead.name", description: "Nombre del prospecto" },
  { path: "lead.email", description: "Email" },
  { path: "lead.phone", description: "Teléfono (con +57)" },
  { path: "lead.whatsappUsername", description: "Nombre del perfil de WhatsApp" },
  {
    path: "lead.whatsappUserId",
    description: "ID de WhatsApp de Meta (BSUID) — existe aunque no haya teléfono",
  },
  { path: "lead.type", description: "Tipo de prospecto (inbound, outbound, pauta)" },
  { path: "lead.status", description: "Estado (active, inactive, converted)" },
  { path: "lead.source", description: "Fuente" },
  { path: "lead.pipelinePositionId", description: "Etapa del embudo" },
  { path: "lead.transferReason", description: "Razón de transferencia" },
  { path: "lead.transferCategory", description: "Categoría de transferencia" },
  { path: "lead.crmId", description: "ID en el CRM (si existe)" },
  { path: "lead.dashboardUrl", description: "Link al prospecto en Palomma" },
  { path: "lead.employeeId", description: "ID del asesor asignado (ULID Palomma)" },
  {
    path: "lead.employee.crmId",
    description: "ID del asesor en el CRM (ASSIGNED_BY_ID en Bitrix)",
  },
  { path: "lead.employee.name", description: "Nombre del asesor" },
  { path: "lead.employee.email", description: "Email del asesor" },
  { path: "lead.employee.phone", description: "Teléfono del asesor" },
  { path: "lead.profile.propertyCode", description: "Código del inmueble buscado" },
  { path: "lead.profile.cities", description: "Ciudades (lista)" },
  { path: "lead.profile.neighborhoods", description: "Barrios (lista)" },
  { path: "lead.profile.neighborhoodList", description: "Barrios (lista alterna)" },
  { path: "lead.profile.propertyTypes", description: "Tipos de inmueble (lista)" },
  { path: "lead.profile.bedrooms", description: "Habitaciones" },
  { path: "lead.profile.bathrooms", description: "Baños" },
  { path: "lead.profile.garages", description: "Parqueaderos" },
  { path: "lead.profile.price.min", description: "Precio mínimo" },
  { path: "lead.profile.price.max", description: "Precio máximo" },
  { path: "lead.profile.sqMeters.min", description: "Metros cuadrados mínimos" },
  { path: "lead.profile.sqMeters.max", description: "Metros cuadrados máximos" },
  { path: "lead.profile.socialStratum", description: "Estrato social (lista)" },
  { path: "lead.profile.listingType", description: "Tipo de listado (rent / sale)" },
  { path: "appointment.startAt", description: "Fecha/hora de inicio de la cita" },
  { path: "appointment.endAt", description: "Fecha/hora fin de la cita" },
  { path: "appointment.listingCode", description: "Código del inmueble de la cita" },
  { path: "appointment.branchName", description: "Sucursal" },
  { path: "appointment.city", description: "Ciudad de la cita" },
  { path: "appointment.neighborhood", description: "Barrio (puede venir con coma)" },
  {
    path: "listingRequest.id",
    description: "ID de la solicitud de captación (ULID)",
  },
  {
    path: "listingRequest.listingType",
    description: "Tipo de operación de la captación (rent / sale / rent_and_sale)",
  },
  {
    path: "listingRequest.propertyType",
    description: "Tipo de inmueble de la captación (apartment, house, …)",
  },
  {
    path: "listingRequest.status",
    description: "Estado del inmueble en la captación (ocupado / desocupado)",
  },
  { path: "listingRequest.city", description: "Ciudad de la captación" },
  { path: "listingRequest.neighborhood", description: "Barrio de la captación" },
  { path: "listingRequest.address", description: "Dirección de la captación" },
  {
    path: "listingRequest.expectedRent",
    description: "Canon esperado por el propietario (mensual)",
  },
  {
    path: "listingRequest.isInCoveredArea",
    description: "¿La captación está dentro de la zona de cobertura?",
  },
  {
    path: "listingRequest.createdAt",
    description: "Fecha en que se registró la captación",
  },
];

export type BitrixKnownValue = { value: string; label?: string };

// Values sourced from valueMap.ts (runtime import from backend) and backend
// tuples directly. When a path shows up here, the switch-case `match` input
// renders suggestions + a hint chip. Paths NOT in this map stay free-form.
const toKnownValues = (map: Record<string, string>): BitrixKnownValue[] =>
  Object.entries(map).map(([value, label]) => ({ value, label }));

export const BITRIX_PATH_KNOWN_VALUES: Record<string, readonly BitrixKnownValue[]> = {
  "lead.type": toKnownValues(mapLeadType),
  "lead.status": [
    { value: "active", label: "Activo" },
    { value: "inactive", label: "Inactivo" },
    { value: "converted", label: "Convertido" },
  ],
  "lead.source": toKnownValues(mapSource),
  "lead.pipelinePositionId": toKnownValues(mapPipelinePositionId),
  "lead.profile.listingType": listingTypes.map((v) => ({ value: v })),
  "lead.profile.propertyTypes": propertyTypes.map((v) => ({ value: v })),
  "listingRequest.listingType": listingTypes.map((v) => ({ value: v })),
  "listingRequest.propertyType": propertyTypes.map((v) => ({ value: v })),
  "listingRequest.status": [
    { value: "ocupado", label: "Ocupado" },
    { value: "desocupado", label: "Desocupado" },
  ],
};

export const getKnownValuesForPath = (
  path: string,
): readonly BitrixKnownValue[] | undefined => BITRIX_PATH_KNOWN_VALUES[path];

export const BITRIX_PHONE_VALUE_TYPES = ["MOBILE", "WORK", "HOME", "OTHER"] as const;
export const BITRIX_EMAIL_VALUE_TYPES = ["WORK", "HOME", "OTHER"] as const;

// Top-level Bitrix field names commonly mapped on Deals (and a few Lead ones).
// Surface these as autocomplete suggestions in the standardFields editor so
// merchants don't have to memorize Bitrix field names. Free-form is allowed.
export const BITRIX_STANDARD_FIELD_SUGGESTIONS: Array<{
  key: string;
  description: string;
}> = [
  { key: "CATEGORY_ID", description: "ID del embudo (Deal)" },
  { key: "TYPE_ID", description: "Tipo de Deal o SPA (p. ej. UC_XQVPTD)" },
  { key: "STAGE_ID", description: "Stage del Deal (p. ej. C18:NEW)" },
  { key: "OPPORTUNITY", description: "Monto del Deal" },
  { key: "CURRENCY_ID", description: "Moneda del Deal (p. ej. COP)" },
  { key: "STATUS_ID", description: "Estado (Lead) o estado del Deal" },
  { key: "OPENED", description: "¿Visible para todos? (Y / N)" },
  { key: "BEGINDATE", description: "Fecha de inicio (Deal)" },
  { key: "CLOSEDATE", description: "Fecha de cierre estimada (Deal)" },
  { key: "ADDITIONAL_INFO", description: "Información adicional" },
];

export const BITRIX_CREATE_DEFAULTS: BitrixCreateAction = {
  title: "Palomma - {{lead.name}}",
  comments: "Creado desde Palomma",
  contactPhone: { mode: "path", path: "lead.phone" },
  contactEmail: { mode: "path", path: "lead.email" },
  contactDefaults: {
    fallbackLastName: "palomma",
    phoneValueType: "MOBILE",
    emailValueType: "WORK",
  },
  customFields: {},
};

export const BITRIX_UPDATE_DEFAULTS: BitrixUpdateAction = {
  customFields: {},
};

export const getExprMode = (expr: BitrixFieldExpr): BitrixExprMode => {
  if (expr === null || typeof expr !== "object") return "literal";
  if (expr.mode === "path") return "path";
  if (expr.mode === "switch") return "switch";
  if (expr.mode === "lookup") return "lookup";
  return "if";
};

export const emptyExprForMode = (mode: BitrixExprMode): BitrixFieldExpr => {
  switch (mode) {
    case "literal":
      return "";
    case "path":
      return { mode: "path", path: "" };
    case "switch":
      return {
        mode: "switch",
        source: { path: "", transform: { type: "none" } },
        cases: [],
        default: null,
      };
    case "if":
      return {
        mode: "if",
        condition: { path: "", operator: "exists" },
        then: { kind: "literal", value: "" },
        else: { kind: "literal", value: null },
      };
    case "lookup":
      return {
        mode: "lookup",
        keys: [{ path: "" }],
        rows: [],
        default: null,
      };
  }
};

export const BITRIX_IF_OPERATORS: Array<{
  id: BitrixIfOperator;
  label: string;
  requiresCompareValue: boolean;
}> = [
  { id: "equals", label: "es igual a", requiresCompareValue: true },
  { id: "notEquals", label: "es distinto de", requiresCompareValue: true },
  { id: "greaterThan", label: "es mayor que", requiresCompareValue: true },
  { id: "lessThan", label: "es menor que", requiresCompareValue: true },
  { id: "greaterOrEqual", label: "es mayor o igual a", requiresCompareValue: true },
  { id: "lessOrEqual", label: "es menor o igual a", requiresCompareValue: true },
  { id: "exists", label: "tiene valor", requiresCompareValue: false },
  { id: "notExists", label: "está vacío", requiresCompareValue: false },
];
